'use client'

import { useLazyQuery, useQuery } from '@apollo/client/react'
import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  HStack,
  Input,
  Separator,
  Spinner,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaPlus, FaTrash } from 'react-icons/fa'
import { useDebounce } from 'react-use'

import { NewCircleWizard } from '@/components/molecules/Form/Circles/CircleForm/newCircle/NewCircleWizard'
import { StepperDiptychLayout } from '@/components/templates/StepperDiptychLayout'
import { Avatar } from '@/components/ui/avatar'
import { Button as UIButton } from '@/components/ui/button'
import { useColorModeValue } from '@/components/ui/color-mode'
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogRoot,
} from '@/components/ui/dialog'
import {
  NetworkNewWizard_MyCirclesDocument,
  NetworkNewWizard_SearchCirclesDocument,
} from '@/graphql/api/generated'

import { NetworkCardWizardCompositionPreview } from './NetworkCardWizardCompositionPreview'
import { NetworkNewWizardStepNav } from './NetworkNewWizardStepNav'
import type { NetworkWizardFormValues } from './networkWizardTypes'

function NetworkNewWizardCompositionFields() {
  const { t } = useTranslation()
  const cardActiveBorder = useColorModeValue('gray.900', 'brand.400')
  const cardActiveBg = useColorModeValue('white', 'gray.700')
  const cardIdleBorder = useColorModeValue('blackAlpha.400', 'whiteAlpha.300')
  const cardIdleBg = useColorModeValue('whiteAlpha.600', 'gray.900')
  const cardHoverIdleBorder = useColorModeValue(
    'blackAlpha.500',
    'whiteAlpha.400'
  )
  const cardHoverIdleBg = useColorModeValue('whiteAlpha.800', 'gray.800')
  const emptyStateBorder = useColorModeValue('blackAlpha.200', 'whiteAlpha.200')
  const { watch, setValue, getValues } =
    useFormContext<NetworkWizardFormValues>()
  const managedCircleIris = watch('managedCircleIris')
  const externalCircles = watch('externalCircles')

  const { data: allMyCirclesData, refetch: refetchAllManagedCircles } =
    useQuery(NetworkNewWizard_MyCirclesDocument, {
      variables: { disabled: false, searchTerms: undefined },
    })

  const [managedSearchInput, setManagedSearchInput] = useState('')
  const [debouncedManagedSearch, setDebouncedManagedSearch] = useState('')
  useDebounce(() => setDebouncedManagedSearch(managedSearchInput.trim()), 400, [
    managedSearchInput,
  ])

  const {
    data: managedListData,
    loading: loadingManagedCircles,
    refetch: refetchManagedCirclesList,
  } = useQuery(NetworkNewWizard_MyCirclesDocument, {
    variables: {
      disabled: false,
      searchTerms:
        debouncedManagedSearch === '' ? undefined : debouncedManagedSearch,
    },
    notifyOnNetworkStatusChange: true,
  })

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const managedCircleIdsBeforeCreateRef = useRef<string[]>([])

  const managedSearchLoading =
    loadingManagedCircles && debouncedManagedSearch !== ''

  const allManagedNodes = useMemo(
    () =>
      allMyCirclesData?.myCircles?.edges
        ?.map((e) => e?.node)
        .filter((n): n is NonNullable<typeof n> => Boolean(n)) ?? [],
    [allMyCirclesData?.myCircles?.edges]
  )

  const myCircles = useMemo(
    () =>
      managedListData?.myCircles?.edges
        ?.map((e) => e?.node)
        .filter((n): n is NonNullable<typeof n> => Boolean(n)) ?? [],
    [managedListData?.myCircles?.edges]
  )

  const [searchTerms, setSearchTerms] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useDebounce(() => setDebouncedSearch(searchTerms.trim()), 400, [searchTerms])

  const [runSearch, { data: searchData }] = useLazyQuery(
    NetworkNewWizard_SearchCirclesDocument,
    { fetchPolicy: 'network-only' }
  )

  useEffect(() => {
    if (debouncedSearch.length >= 2) {
      void runSearch({
        variables: { searchTerms: debouncedSearch, first: 15 },
      })
    }
  }, [debouncedSearch, runSearch])

  const searchNodes = useMemo(
    () =>
      searchData?.circles?.edges
        ?.map((e) => e?.node)
        .filter((n): n is NonNullable<typeof n> => Boolean(n)) ?? [],
    [searchData?.circles?.edges]
  )

  const externalIds = useMemo(
    () => new Set(externalCircles.map((x) => x.id)),
    [externalCircles]
  )

  const filteredSearch = useMemo(() => {
    const managed = new Set(managedCircleIris)
    return searchNodes.filter(
      (n) => !managed.has(n.id) && !externalIds.has(n.id)
    )
  }, [searchNodes, managedCircleIris, externalIds])

  const toggleManagedId = useCallback(
    (id: string) => {
      const cur = getValues('managedCircleIris')
      if (cur.includes(id)) {
        setValue(
          'managedCircleIris',
          cur.filter((x) => x !== id)
        )
      } else {
        setValue('managedCircleIris', [...cur, id])
      }
    },
    [getValues, setValue]
  )

  const addExternal = useCallback(
    (node: {
      id: string
      name: string
      slug: string
      logo?: { contentUrl?: string | null } | null
    }) => {
      const cur = getValues('externalCircles')
      if (
        cur.some((x) => x.id === node.id) ||
        managedCircleIris.includes(node.id)
      ) {
        return
      }
      setValue('externalCircles', [
        ...cur,
        {
          id: node.id,
          name: node.name,
          slug: node.slug,
          logoContentUrl: node.logo?.contentUrl ?? undefined,
        },
      ])
    },
    [getValues, managedCircleIris, setValue]
  )

  const removeExternal = useCallback(
    (id: string) => {
      setValue(
        'externalCircles',
        getValues('externalCircles').filter((x) => x.id !== id)
      )
    },
    [getValues, setValue]
  )

  const handleOpenCreateCommunityDialog = useCallback(() => {
    managedCircleIdsBeforeCreateRef.current = allManagedNodes.map((c) => c.id)
    setIsCreateDialogOpen(true)
  }, [allManagedNodes])

  const handleCreateCommunityWizardSuccess = useCallback(async () => {
    setIsCreateDialogOpen(false)
    const [fullListRes] = await Promise.all([
      refetchAllManagedCircles(),
      refetchManagedCirclesList(),
    ])
    const edges = fullListRes.data?.myCircles?.edges ?? []
    const afterIds = edges
      .map((e) => e?.node?.id)
      .filter((id): id is string => Boolean(id))
    const newId = afterIds.find(
      (id) => !managedCircleIdsBeforeCreateRef.current.includes(id)
    )
    if (newId) {
      const cur = getValues('managedCircleIris')
      if (!cur.includes(newId)) {
        setValue('managedCircleIris', [...cur, newId])
      }
    }
  }, [getValues, refetchAllManagedCircles, refetchManagedCirclesList, setValue])

  return (
    <VStack gap={8} w="full" align="stretch">
      <Stack gap={3}>
        <Text fontWeight="semibold">
          {t('networks.wizard.composition.managed.title')}
        </Text>
        <Text fontSize="sm" color="fg.muted">
          {t('networks.wizard.composition.managed.helper')}
        </Text>

        {allManagedNodes.length === 0 ? (
          <Center
            rounded="xl"
            borderWidth="1px"
            borderColor={emptyStateBorder}
            py={10}
            px={4}
          >
            <VStack gap={4} maxW="md" textAlign="center" align="center">
              <Text fontSize="md" fontWeight="semibold">
                {t('networks.wizard.composition.managed.emptyStateTitle')}
              </Text>
              <Text fontSize="sm" color="fg.muted">
                {t('networks.wizard.composition.managed.emptyStateHint')}
              </Text>
              <UIButton
                colorPalette="brand"
                onClick={handleOpenCreateCommunityDialog}
              >
                <HStack gap={2}>
                  <FaPlus />
                  <span>{t('networks.edit.communities.create.button')}</span>
                </HStack>
              </UIButton>
            </VStack>
          </Center>
        ) : (
          <Stack gap={3}>
            <Input
              value={managedSearchInput}
              onChange={(e) => setManagedSearchInput(e.target.value)}
              placeholder={t(
                'networks.wizard.composition.managed.searchPlaceholder'
              )}
            />
            <Box maxH="min(280px, 45vh)" overflowY="auto" pr={1}>
              {managedSearchLoading ? (
                <Center py={10}>
                  <Spinner size="sm" />
                </Center>
              ) : myCircles.length === 0 && debouncedManagedSearch !== '' ? (
                <Text fontSize="sm" color="fg.muted" py={2}>
                  {t('networks.wizard.composition.managed.noMatch')}
                </Text>
              ) : (
                <Stack gap={2}>
                  {myCircles.map((c) => {
                    const active = managedCircleIris.includes(c.id)
                    return (
                      <Box
                        key={c.id}
                        role="button"
                        tabIndex={0}
                        aria-pressed={active}
                        aria-label={
                          active
                            ? `${c.name} — ${t('networks.wizard.composition.managed.toggleRemove')}`
                            : `${c.name} — ${t('networks.wizard.composition.managed.toggleAdd')}`
                        }
                        onClick={() => toggleManagedId(c.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            toggleManagedId(c.id)
                          }
                        }}
                        borderWidth={active ? '2px' : '1px'}
                        rounded="lg"
                        px={3}
                        py={2.5}
                        cursor="pointer"
                        transition="border-color 0.15s, background 0.15s"
                        borderColor={active ? cardActiveBorder : cardIdleBorder}
                        bg={active ? cardActiveBg : cardIdleBg}
                        _hover={
                          active
                            ? { borderColor: cardActiveBorder }
                            : {
                                borderColor: cardHoverIdleBorder,
                                bg: cardHoverIdleBg,
                              }
                        }
                        _focusVisible={{
                          outline: '2px solid',
                          outlineColor: cardActiveBorder,
                          outlineOffset: '2px',
                        }}
                      >
                        <HStack gap={3} align="center">
                          <Avatar
                            size="sm"
                            name={c.name}
                            src={c.logo?.contentUrl ?? undefined}
                            flexShrink={0}
                          />
                          <VStack align="flex-start" gap={0} flex={1} minW={0}>
                            <Text fontSize="sm" fontWeight="medium" truncate>
                              {c.name}
                            </Text>
                            <Text fontSize="xs" color="fg.muted" truncate>
                              {c.slug}
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>
                    )
                  })}
                </Stack>
              )}
            </Box>
            <Flex justify={{ base: 'stretch', md: 'flex-start' }} pt={1}>
              <UIButton
                colorPalette="brand"
                variant="outline"
                onClick={handleOpenCreateCommunityDialog}
              >
                <HStack gap={2}>
                  <FaPlus />
                  <span>{t('networks.edit.communities.create.button')}</span>
                </HStack>
              </UIButton>
            </Flex>
          </Stack>
        )}
      </Stack>

      <DialogRoot
        lazyMount
        open={isCreateDialogOpen}
        onOpenChange={(e) => setIsCreateDialogOpen(e.open)}
        placement="center"
      >
        <DialogContent minWidth="3xl" maxWidth="6xl">
          <DialogCloseTrigger />
          <DialogBody>
            <NewCircleWizard
              title={t('networks.edit.communities.create.dialog.title')}
              onSuccess={handleCreateCommunityWizardSuccess}
            />
          </DialogBody>
        </DialogContent>
      </DialogRoot>

      <Separator />

      <Stack gap={3}>
        <Text fontWeight="semibold">
          {t('networks.wizard.composition.external.title')}
        </Text>
        <Text fontSize="sm" color="fg.muted">
          {t('networks.wizard.composition.external.helper')}
        </Text>
        <Input
          value={searchTerms}
          onChange={(e) => setSearchTerms(e.target.value)}
          placeholder={t(
            'networks.wizard.composition.external.searchPlaceholder'
          )}
        />
        {filteredSearch.length > 0 && (
          <Stack gap={1.5} w="full" minW={0} maxW="100%">
            {filteredSearch.map((n) => (
              <Grid
                key={n.id}
                templateColumns="auto minmax(0,1fr) auto"
                gap={2}
                alignItems="center"
                w="full"
                minW={0}
                maxW="100%"
              >
                <Avatar
                  size="sm"
                  name={n.name}
                  src={n.logo?.contentUrl ?? undefined}
                  flexShrink={0}
                />
                <Text fontSize="sm" truncate minW={0}>
                  {n.name}
                </Text>
                <Button
                  size="xs"
                  variant="outline"
                  flexShrink={0}
                  onClick={() => addExternal(n)}
                >
                  <FaPlus /> {t('global:add')}
                </Button>
              </Grid>
            ))}
          </Stack>
        )}
        {externalCircles.length > 0 && (
          <Stack gap={2} w="full" minW={0} maxW="100%">
            {externalCircles.map((c) => (
              <Grid
                key={c.id}
                templateColumns="auto minmax(0,1fr) auto"
                gap={2}
                alignItems="center"
                borderWidth="1px"
                rounded="md"
                p={2}
                w="full"
                minW={0}
                maxW="100%"
              >
                <Avatar
                  size="sm"
                  name={c.name}
                  src={c.logoContentUrl}
                  flexShrink={0}
                />
                <Text fontSize="sm" truncate minW={0}>
                  {c.name}
                </Text>
                <Button
                  size="xs"
                  variant="ghost"
                  flexShrink={0}
                  aria-label={t('global:delete')}
                  onClick={() => removeExternal(c.id)}
                >
                  <FaTrash />
                </Button>
              </Grid>
            ))}
          </Stack>
        )}
      </Stack>
    </VStack>
  )
}

export const NetworkNewWizardCompositionStep = () => {
  const { t } = useTranslation()

  return (
    <StepperDiptychLayout
      title={t('networks.wizard.steps.composition.title')}
      description={t('networks.wizard.steps.composition.description')}
      shellGradient
      rightContentAlignment="center"
      leftElement={
        <Stack
          gap={{ base: 6, md: 8 }}
          w="full"
          maxW={{ base: 'full', lg: '3xl' }}
          maxH={{ base: 'min(72vh, 720px)', lg: 'none' }}
          overflowY={{ base: 'auto', lg: 'visible' }}
          alignSelf="stretch"
        >
          <NetworkNewWizardCompositionFields />
          <NetworkNewWizardStepNav />
        </Stack>
      }
      rightElement={<NetworkCardWizardCompositionPreview />}
    />
  )
}
